import shaka from "shaka-player/dist/shaka-player.ui";
import { ShakaP2PEngine } from "p2p-media-loader-shaka";
import { registerP2PPlugins, createP2PEngine, getP2PStats, resetP2PStats, onStatsChange, consumeSegmentLoadDuration, getP2PBufferAhead, type P2PStats } from "./p2p";
import { encodeFromUUID } from "./base58";

// Import Shaka UI styles
import "shaka-player/dist/controls.css";

// P2P Button for Shaka UI
class P2PButton extends shaka.ui.Element {
  private button: HTMLButtonElement;
  private panel: HTMLDivElement;
  private statsContent: HTMLDivElement;
  private isPanelOpen = false;

  constructor(parent: HTMLElement, controls: shaka.ui.Controls) {
    super(parent, controls);

    // Create button
    this.button = document.createElement("button");
    this.button.className = "shaka-p2p-button";
    this.button.setAttribute("aria-label", "P2P Stats");
    this.button.innerHTML = `
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
      </svg>
    `;

    // Create stats panel
    this.panel = document.createElement("div");
    this.panel.className = "shaka-p2p-panel";
    this.panel.innerHTML = `
      <div class="shaka-p2p-panel-header">P2P Statistics</div>
      <div class="shaka-p2p-panel-content"></div>
    `;
    this.statsContent = this.panel.querySelector(".shaka-p2p-panel-content")!;
    this.updateStatsDisplay(getP2PStats());

    // Add panel inside button, then button to parent
    this.button.appendChild(this.panel);
    parent.appendChild(this.button);

    // Event listeners
    this.button.addEventListener("click", () => this.togglePanel());

    // Prevent panel clicks from toggling the button
    this.panel.addEventListener("click", (e) => e.stopPropagation());

    // Close panel when clicking outside
    document.addEventListener("click", (e) => {
      if (this.isPanelOpen && !this.button.contains(e.target as Node) && !this.panel.contains(e.target as Node)) {
        this.closePanel();
      }
    });

    // Update stats
    onStatsChange((stats) => this.updateStats(stats));
  }

  private togglePanel(): void {
    this.isPanelOpen = !this.isPanelOpen;
    this.panel.classList.toggle("open", this.isPanelOpen);
  }

  private closePanel(): void {
    this.isPanelOpen = false;
    this.panel.classList.remove("open");
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private updateStats(stats: P2PStats): void {
    // Update button style based on peer count
    this.button.classList.toggle("has-peers", stats.peersConnected > 0);

    // Update panel content
    this.updateStatsDisplay(stats);
  }

  private updateStatsDisplay(stats: P2PStats): void {
    this.statsContent.innerHTML = `
      <div class="shaka-p2p-stat">
        <span class="shaka-p2p-stat-label">Connected Peers</span>
        <span class="shaka-p2p-stat-value peers">${stats.peersConnected}</span>
      </div>
      <div class="shaka-p2p-stat">
        <span class="shaka-p2p-stat-label">HTTP Download</span>
        <span class="shaka-p2p-stat-value http">${this.formatBytes(stats.bytesDownloadedHTTP)}</span>
      </div>
      <div class="shaka-p2p-stat">
        <span class="shaka-p2p-stat-label">P2P Download</span>
        <span class="shaka-p2p-stat-value p2p">${this.formatBytes(stats.bytesDownloadedP2P)}</span>
      </div>
      <div class="shaka-p2p-stat">
        <span class="shaka-p2p-stat-label">P2P Upload</span>
        <span class="shaka-p2p-stat-value upload">${this.formatBytes(stats.bytesUploaded)}</span>
      </div>
    `;
  }
}

// Factory for P2P button
class P2PButtonFactory {
  create(rootElement: HTMLElement, controls: shaka.ui.Controls): shaka.ui.Element {
    return new P2PButton(rootElement, controls);
  }
}

// Register the P2P button
shaka.ui.Controls.registerElement("p2p", new P2PButtonFactory());

// --- Caption size customization ---
// Stored globally so the choice persists across videos and across page loads.
type CaptionSize = "small" | "medium" | "large";
const CAPTION_SIZE_KEY = "dtube-caption-size";
const CAPTION_SIZE_DEFAULT: CaptionSize = "medium";
const CAPTION_SIZE_LABEL: Record<CaptionSize, string> = {
  small: "Small",
  medium: "Medium",
  large: "Large",
};
// em multiplier against the browser's default ::cue font-size
// (~5% of video height in Chrome/Firefox).
const CAPTION_SIZE_EM: Record<CaptionSize, string> = {
  small: "0.55em",
  medium: "0.75em",
  large: "1em",
};
const CAPTION_STYLE_ID = "dtube-cue-style";

function loadCaptionSize(): CaptionSize {
  try {
    const v = localStorage.getItem(CAPTION_SIZE_KEY);
    if (v === "small" || v === "medium" || v === "large") return v;
  } catch {}
  return CAPTION_SIZE_DEFAULT;
}

function saveCaptionSize(size: CaptionSize): void {
  try { localStorage.setItem(CAPTION_SIZE_KEY, size); } catch {}
}

function applyCaptionSize(size: CaptionSize): void {
  let style = document.getElementById(CAPTION_STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = CAPTION_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = `::cue { font-size: ${CAPTION_SIZE_EM[size]}; }`;
}

// Material "format_size" SVG path (the Aa-with-size-indicator glyph).
// Shaka's iconText param is the raw `d` attribute. Path is in classic
// Material 24x24 coords — we override the SVG's viewBox below since
// Shaka's bundled icons use the newer Material Symbols 960x960 viewBox.
const FORMAT_SIZE_PATH = "M9 4v3h5v12h3V7h5V4H9zm-6 8h3v7h3v-7h3V9H3v3z";

class CaptionSizeButton extends shaka.ui.SettingsMenu {
  constructor(parent: HTMLElement, controls: shaka.ui.Controls) {
    super(parent, controls, FORMAT_SIZE_PATH);
    this.icon.getSvgElement()?.setAttribute("viewBox", "0 0 24 24");

    this.nameSpan.textContent = "Caption size";
    this.backSpan.textContent = "Caption size";
    const current = loadCaptionSize();
    this.currentSelection.textContent = CAPTION_SIZE_LABEL[current];

    const sizes: CaptionSize[] = ["small", "medium", "large"];
    for (const size of sizes) {
      const item = document.createElement("button");
      item.classList.add("shaka-enable-button");
      const label = document.createElement("span");
      label.textContent = CAPTION_SIZE_LABEL[size];
      item.appendChild(label);
      if (size === current) item.classList.add("shaka-chosen-item");
      item.addEventListener("click", () => {
        applyCaptionSize(size);
        saveCaptionSize(size);
        this.currentSelection.textContent = CAPTION_SIZE_LABEL[size];
        this.menu.querySelectorAll(".shaka-enable-button").forEach((b) =>
          b.classList.toggle("shaka-chosen-item", b === item),
        );
      });
      this.menu.appendChild(item);
    }
  }
}

class CaptionSizeButtonFactory {
  create(rootElement: HTMLElement, controls: shaka.ui.Controls): shaka.ui.Element {
    return new CaptionSizeButton(rootElement, controls);
  }
}

// Items inside the overflow (cog) menu register on OverflowMenu, not Controls.
shaka.ui.OverflowMenu.registerElement("caption_size", new CaptionSizeButtonFactory());

export interface DTubePlayerOptions {
  src?: string;
  poster?: string;
  videoId?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  showLogo?: boolean;
  onReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onError?: (error: Error) => void;
}

const VOLUME_STORAGE_KEY = "dtube-player-volume";
const BANDWIDTH_STORAGE_KEY = "dtube-player-bandwidth";
const DEFAULT_BANDWIDTH_ESTIMATE = 5_000_000;
const MIN_SAVED_BANDWIDTH = 1_000_000;
const MAX_SAVED_BANDWIDTH = 50_000_000;
const DTUBE_PLAYER_VERSION = "0.31.0";
const CAST_RECEIVER_APP_ID = "E0A1E93C"; // DTube custom Shaka receiver (play.d.tube/receiver.html)

// Register P2P plugins on module load
registerP2PPlugins();

export class DTubePlayer {
  private container: HTMLElement;
  private wrapper: HTMLDivElement | null = null;
  private options: DTubePlayerOptions;
  private video: HTMLVideoElement | null = null;
  private player: shaka.Player | null = null;
  private ui: shaka.ui.Overlay | null = null;
  private p2pEngine: ShakaP2PEngine | null = null;
  private retryingWithoutAudio = false;
  private streamingRetryCount = 0;
  private lastSrc: string | null = null;
  private castOverlay: HTMLDivElement | null = null;
  private nerdStatsPanel: HTMLDivElement | null = null;
  private nerdStatsInterval: ReturnType<typeof setInterval> | null = null;
  private bandwidthSaveInterval: ReturnType<typeof setInterval> | null = null;
  private lastFrameCount = 0;
  private lastFrameTime = 0;
  private currentFps = 0;
  private segmentLog: { time: number; bytes: number }[] = [];
  private base58Id: string | undefined;

  constructor(
    elementOrSelector: string | HTMLElement,
    options: DTubePlayerOptions = {}
  ) {
    this.options = options;
    try { this.base58Id = options.videoId ? encodeFromUUID(options.videoId) : undefined; } catch { this.base58Id = options.videoId; }

    if (typeof elementOrSelector === "string") {
      const el = document.querySelector(elementOrSelector);
      if (!el) throw new Error(`Element not found: ${elementOrSelector}`);
      this.container = el as HTMLElement;
    } else {
      this.container = elementOrSelector;
    }

    this.initialize();
  }

  private async initialize(): Promise<void> {
    // Reset P2P stats for new player
    resetP2PStats();

    // Install Shaka polyfills
    shaka.polyfill.installAll();

    // Check browser support
    if (!shaka.Player.isBrowserSupported()) {
      this.options.onError?.(new Error("Browser not supported"));
      return;
    }

    // Create wrapper for Shaka UI (no data-shaka-player-container to avoid auto-init)
    this.wrapper = document.createElement("div");
    this.wrapper.className = "dtube-player-wrapper";
    this.container.appendChild(this.wrapper);

    // Create video element
    this.video = document.createElement("video");
    this.video.className = "dtube-video";
    this.video.playsInline = true;
    this.video.crossOrigin = "anonymous";

    if (this.options.poster) {
      this.video.poster = this.options.poster;
    }
    if (this.options.autoplay) {
      this.video.autoplay = true;
    }
    if (this.options.muted) {
      this.video.muted = true;
    }
    if (this.options.loop) {
      this.video.loop = true;
    }

    this.wrapper.appendChild(this.video);

    // Create Shaka player and attach to video
    this.player = new shaka.Player();
    await this.player.attach(this.video);

    this.player.configure({
      abr: {
        defaultBandwidthEstimate: this.loadBandwidthEstimate(),
        bandwidthUpgradeTarget: 0.75,
        switchInterval: 4,
        useNetworkInformation: false,
        cacheLoadThreshold: 50,
      },
      streaming: {
        bufferingGoal: 11,
        // Shaka's default is maxAttempts: 2 — one hiccup ~1s apart and a
        // 2-hour movie dies. Give transient failures room to recover.
        retryParameters: {
          maxAttempts: 4,
          baseDelay: 1000,
          backoffFactor: 2,
        },
      },
    });

    this.bandwidthSaveInterval = setInterval(() => this.saveBandwidthEstimate(), 10000);

    // Correct timeMs for p2p-media-loader responses (it rounds to whole seconds,
    // usually 0). We prefer the p2p core's own onSegmentStart/onSegmentLoaded
    // timing since it captures real download time even for segments the core
    // pre-fetched before Shaka asked for them. For segments that go through
    // defaultLoad (bypassing the core), we fall back to our own wall-clock
    // measurement between request and response filters.
    const requestStarts = new Map<string, number>();
    const netEngine = this.player.getNetworkingEngine();
    netEngine?.registerRequestFilter((_type, request) => {
      for (const uri of request.uris) requestStarts.set(uri, Date.now());
    });
    netEngine?.registerResponseFilter((_type, response) => {
      if (!response.data) return;
      const coreDuration = consumeSegmentLoadDuration(response.uri);
      if (coreDuration !== undefined && coreDuration > 0) {
        response.timeMs = coreDuration;
      } else {
        const start = requestStarts.get(response.uri);
        if (start !== undefined) {
          const measured = Date.now() - start;
          if (!response.timeMs || response.timeMs < measured) {
            response.timeMs = measured;
          }
        }
      }
      requestStarts.delete(response.uri);
      this.segmentLog.push({ time: Date.now(), bytes: response.data.byteLength });
    });

    // Create P2P engine BEFORE loading content
    if (this.options.videoId) {
      this.p2pEngine = createP2PEngine(this.options.videoId);
      this.p2pEngine.bindShakaPlayer(this.player as unknown as Parameters<typeof this.p2pEngine.bindShakaPlayer>[0]);
    }

    // Create Shaka UI overlay. Shaka's Controls builds its own CastProxy
    // internally from castReceiverAppId — passing the ID via UI config is
    // what makes the built-in cast button discover and route to receivers.
    // P2P stays bound to the local player and goes idle during a cast
    // session (the receiver fetches HLS directly from the CDN).
    this.ui = new shaka.ui.Overlay(this.player, this.wrapper, this.video);

    this.ui.configure({
      castReceiverAppId: CAST_RECEIVER_APP_ID,
      castAndroidReceiverCompatible: false,
      addBigPlayButton: false,
      controlPanelElements: [
        "play_pause",
        "time_and_duration",
        "mute",
        "volume",
        "spacer",
        "p2p",
        "cast",
        "overflow_menu",
        "fullscreen",
      ],
      overflowMenuButtons: [
        // "captions" is added dynamically by attachTranscript() when a VTT exists
        "quality",
        "playback_rate",
        "picture_in_picture",
      ],
    });

    // Ensure saved caption size is applied before any cue renders.
    applyCaptionSize(loadCaptionSize());

    this.installCastBehavior();

    // Add DTube logo
    if (this.options.showLogo !== false) {
      this.addLogo();
    }

    // Add custom big play button
    this.addBigPlayButton();

    // Add right-click context menu
    this.addContextMenu();

    // Bind events
    this.bindEvents();

    // Load source if provided
    if (this.options.src) {
      this.lastSrc = this.options.src;
      try {
        await this.getActivePlayer().load(this.options.src);
        this.restoreVolume();
        this.attachTranscript();
        this.options.onReady?.();
      } catch (error) {
        console.error("Error loading video:", error);
        this.options.onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    } else {
      this.options.onReady?.();
    }
  }

  // Attach the WebVTT transcript as a text track. Always pulled from nas1
  // because nas2 doesn't mirror /transcripts/ yet — revisit once the sync
  // covers them. Fetches the VTT first to (a) check it exists and (b) read
  // the "NOTE Language: xx" header written by ai/whisper/transcribe.py so the
  // captions menu shows the real language instead of "Undetermined". The
  // captions button is added to the overflow menu only when a track
  // successfully loads. Must run after every fresh .load() since Shaka
  // clears text tracks on reload.
  private async attachTranscript(): Promise<void> {
    // Reset menu in case we're swapping from a video that had a transcript
    // to one that doesn't.
    this.setCaptionsMenuVisible(false);

    const videoId = this.options.videoId;
    if (!videoId || !this.player) return;
    const url = `https://nas1.d.tube/transcripts/${videoId}.vtt`;

    let vttText: string;
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      vttText = await res.text();
    } catch {
      return;
    }

    const lang = vttText.match(/^NOTE Language:\s*(\S+)/m)?.[1] ?? "und";

    try {
      await this.getActivePlayer().addTextTrackAsync(url, lang, "captions", "text/vtt");
      this.setCaptionsMenuVisible(true);
    } catch {
      // Track failed to load — leave menu hidden
    }
  }

  private setCaptionsMenuVisible(visible: boolean): void {
    if (!this.ui) return;
    const base = ["quality", "playback_rate", "picture_in_picture"];
    this.ui.configure({
      overflowMenuButtons: visible ? ["captions", "caption_size", ...base] : base,
    });
  }

  private addLogo(): void {
    if (!this.wrapper) return;

    const logo = document.createElement("a");
    logo.className = "dtube-logo";
    logo.href = this.base58Id
      ? `https://d.tube/watch/${this.base58Id}`
      : "https://d.tube";
    logo.target = "_blank";
    logo.rel = "noopener noreferrer";
    logo.innerHTML = `
      <svg class="dtube-logo-icon" viewBox="130 130 235 210" xmlns="http://www.w3.org/2000/svg">
        <path fill="#F01A30" d="M336.7,279.4h0.9c14.1,0,25.6,11.5,25.6,25.6v0c0,14.1-11.5,25.6-25.6,25.6h-0.9c-14.1,0-25.6-11.5-25.6-25.6v0C311.1,290.9,322.6,279.4,336.7,279.4z"/>
        <path fill="#FFFFFF" d="M304,197.3c-4.4-12.2-10.5-22.6-18.5-31.2c-7.9-8.6-17.5-15.2-28.6-20c-11.1-4.7-23.4-7.1-36.9-7.1h-83.4v195.6h80.4c12.4,0,23.5-1.5,33.4-4.5c9.8-3,18.6-7.4,26.3-13.3c10.9-8.4,19.3-19.3,25.2-32.7c5.8-13.4,8.8-28.8,8.8-46.1C310.6,223.1,308.4,209.5,304,197.3z M185.7,280.8v-87.9l75.9,44L185.7,280.8z"/>
      </svg>
      <svg class="dtube-logo-full" viewBox="0 0 1459 354.9" xmlns="http://www.w3.org/2000/svg">
        <path fill="#FFFFFF" d="M690,354.9c-31.4,0-55.1-9.3-71.2-27.8c-16.1-18.5-24.1-42.9-24.1-73.3V76.1h85.7v159.8c0,14.8,3.3,26.2,10.1,34.1c6.7,7.9,16.9,11.9,30.7,11.9c14.8,0,26.5-5,34.9-15.1c8.5-10.1,12.7-23.2,12.7-39.4V76.1h86.3V347h-82.5v-33.3H771c-9.9,13.4-21.1,23.6-33.6,30.7C724.9,351.4,709.1,354.9,690,354.9z"/>
        <path fill="#FFFFFF" d="M1327.8,354.3c-21.6,0-41.2-3.5-58.6-10.5c-17.5-7-32.2-16.8-44.2-29.3c-12-12.5-21.3-27.3-27.8-44.5c-6.5-17.1-9.8-35.7-9.8-55.6c0-19.5,3.3-37.8,9.8-54.8c6.5-17,15.8-31.7,27.8-44.2c12-12.5,26.2-22.3,42.7-29.3c16.5-7,34.8-10.5,55-10.5c18.9,0,35.8,2.9,50.7,8.7c14.9,5.8,28,13.9,39.4,24.2c15.8,14.4,27.5,32.7,35.2,54.8c7.7,22.1,11.4,46.6,11.1,73.3h-189.3c2.7,17.8,8.9,31.9,18.5,42.2c9.6,10.3,23.1,15.4,40.6,15.4c11,0,20.1-2.3,27.3-6.9c7.2-4.6,12.7-11.1,16.5-19.3h81.8c-2.7,12-7.9,23.4-15.4,34.2c-7.5,10.8-17.2,20.2-28.8,28c-11,7.9-23.2,13.9-36.8,18C1359.8,352.3,1344.6,354.3,1327.8,354.3z M1374.1,186.6c-1.7-15.8-7-28.3-15.9-37.6c-8.9-9.3-20.2-13.9-34-13.9c-16.1,0-28.5,4.6-37,13.9c-8.6,9.3-14.2,21.8-17,37.6H1374.1z"/>
        <path fill="#FFFFFF" d="M1037.8,75.6c-26,0-51.9,10-72.5,22.7l0-98.3h-84.9v346.9h81.4l0.2-23.7c21.8,14,50.3,24.6,78.2,24.1c73.7-1.3,133.6-62.4,133.5-136.1C1173.5,136.3,1112.7,75.6,1037.8,75.6z M1099.8,212.1c0,33.7-27.3,61-61,61h-2.2c-33.7,0-61-27.3-61-61v0c0-33.7,27.3-61,61-61h2.2C1072.5,151.1,1099.8,178.4,1099.8,212.1L1099.8,212.1z"/>
        <path fill="#F01A30" d="M362.5,254.4h1.7c25.6,0,46.4,20.8,46.4,46.4v0c0,25.6-20.8,46.4-46.4,46.4h-1.7c-25.6,0-46.4-20.8-46.4-46.4v0C316.1,275.2,336.8,254.4,362.5,254.4z"/>
        <path fill="#FFFFFF" d="M303.2,105.6c-7.9-22.1-19.1-41-33.4-56.5c-14.4-15.5-31.6-27.6-51.8-36.2C197.9,4.3,175.6,0,151.1,0H0v354.3h145.7c22.5,0,42.6-2.7,60.4-8.2c17.8-5.5,33.7-13.5,47.6-24c19.8-15.2,35-34.9,45.6-59.2c10.6-24.3,15.9-52.1,15.9-83.5C315.1,152.3,311.2,127.7,303.2,105.6z M88.8,256.8V97.5l137.5,79.7L88.8,256.8z"/>
        <path fill="#FFFFFF" d="M437.9,143.2h-42.4V76.1h42.4V0H522v76.1h49.2v66.8H522v118.3c0,10.6,2.7,17.6,8.2,21.2c5.5,3.5,12.8,5.3,22,5.3c3.9,0,7.6-0.1,11.1-0.3c3.5-0.2,6.2-0.3,7.9-0.3v63c-3.9,1.1-9.8,2-17.7,2.9c-7.9,0.9-17.7,1.3-29.4,1.3c-12.7,0-24.3-1.1-34.7-3.4c-10.4-2.3-19.5-6.3-27.3-11.9c-7.8-5.6-13.8-13.3-18-23c-4.2-9.7-6.4-21.8-6.4-36.2V143.2z"/>
      </svg>
    `;

    this.wrapper.appendChild(logo);
  }

  // Returns the player to call .load() on. While casting, this is the proxy
  // player so loads route to the receiver — without this, a fresh-load (e.g.
  // iframe reload after the user navigates to a new video) only affects the
  // local Shaka and the TV keeps playing whatever was cast before.
  private getActivePlayer(): shaka.Player {
    const proxy = this.ui?.getControls?.()?.getCastProxy?.();
    if (proxy?.isCasting()) return proxy.getPlayer();
    return this.player!;
  }

  private installCastBehavior(): void {
    if (!this.ui || !this.player) return;

    const controls = this.ui.getControls?.();
    const proxy = controls?.getCastProxy?.();
    if (!proxy) return;

    proxy.addEventListener("caststatuschanged", () => {
      const isCasting = proxy.isCasting();
      // Shaka's CastProxy routes user commands to the remote but doesn't
      // pause the local video — without this we'd play on both PC and TV.
      if (isCasting) {
        this.video?.pause();
        this.showCastOverlay(this.getCastDeviceName());

        // Resync the receiver if it's on a different URL — happens when the
        // iframe reloads on navigation and a pre-existing cast session is
        // resumed without anyone telling the receiver to load the new video.
        // Defer briefly to let CastProxy's own LOAD (if any) complete first.
        setTimeout(() => {
          if (!proxy.isCasting() || !this.lastSrc) return;
          const remoteUri = proxy.getPlayer().getAssetUri();
          if (remoteUri !== this.lastSrc) {
            proxy.getPlayer().load(this.lastSrc).catch(() => {});
          }
        }, 1500);
      } else {
        this.hideCastOverlay();
      }
    });
  }

  private getCastDeviceName(): string {
    try {
      const w = window as unknown as {
        cast?: {
          framework?: {
            CastContext?: {
              getInstance(): {
                getCurrentSession(): {
                  getCastDevice(): { friendlyName?: string } | null;
                } | null;
              };
            };
          };
        };
      };
      const session = w.cast?.framework?.CastContext?.getInstance().getCurrentSession();
      return session?.getCastDevice()?.friendlyName || "TV";
    } catch {
      return "TV";
    }
  }

  private showCastOverlay(deviceName: string): void {
    if (!this.wrapper) return;
    if (!this.castOverlay) {
      const overlay = document.createElement("div");
      overlay.className = "dtube-cast-overlay";
      overlay.innerHTML = `
        <svg class="dtube-cast-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11z"/>
        </svg>
        <div class="dtube-cast-message">Casting on <span class="dtube-cast-device"></span></div>
      `;
      this.wrapper.appendChild(overlay);
      this.castOverlay = overlay;
    }
    const deviceEl = this.castOverlay.querySelector(".dtube-cast-device");
    if (deviceEl) deviceEl.textContent = deviceName;
    this.wrapper.classList.add("casting");
  }

  private hideCastOverlay(): void {
    this.castOverlay?.remove();
    this.castOverlay = null;
    this.wrapper?.classList.remove("casting");
  }

  private addBigPlayButton(): void {
    if (!this.wrapper || !this.video) return;

    const button = document.createElement("button");
    button.className = "dtube-big-play-button";
    button.setAttribute("aria-label", "Play");
    button.innerHTML = `
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z"/>
      </svg>
    `;

    button.addEventListener("click", () => {
      this.video?.play();
    });

    // Update visibility based on video state
    const updateVisibility = () => {
      if (!this.video) return;
      const shouldShow = this.video.paused && !this.video.ended && this.video.readyState >= 2;
      button.classList.toggle("hidden", !shouldShow);
    };

    this.video.addEventListener("play", updateVisibility);
    this.video.addEventListener("pause", updateVisibility);
    this.video.addEventListener("ended", updateVisibility);
    this.video.addEventListener("canplay", updateVisibility);

    // Initial state
    updateVisibility();

    this.wrapper.appendChild(button);
  }

  private addContextMenu(): void {
    if (!this.wrapper || !this.video) return;

    const menu = document.createElement("div");
    menu.className = "dtube-context-menu";
    this.wrapper.appendChild(menu);

    const videoId = this.options.videoId;

    const icons: Record<string, string> = {
      "loop": `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 2l4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>`,
      "copy-url": `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`,
      "copy-url-time": `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
      "copy-embed": `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`,
      "stats-nerds": `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>`,
      "about": `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`,
    };

    const renderMenu = () => {
      const isLooping = this.video?.loop ?? false;
      const items: { label: string; id: string; toggle?: boolean }[] = [
        { label: "Loop", id: "loop", toggle: isLooping },
      ];
      if (videoId) {
        items.push({ label: "Copy video URL", id: "copy-url" });
        items.push({ label: "Copy URL at current time", id: "copy-url-time" });
        items.push({ label: "Copy embed code", id: "copy-embed" });
      }
      items.push({ label: "Stats for nerds", id: "stats-nerds" });
      items.push({ label: "About DTube", id: "about" });

      menu.innerHTML = items
        .map((item) => {
          const icon = `<span class="dtube-context-menu-icon">${icons[item.id] || ""}</span>`;
          const checkmark = item.toggle !== undefined
            ? `<span class="dtube-context-menu-check">${item.toggle ? "&#10003;" : ""}</span>`
            : "";
          return `<button class="dtube-context-menu-item" data-action="${item.id}">${icon}<span>${item.label}</span>${checkmark}</button>`;
        })
        .join("");
    };

    const showMenu = (x: number, y: number) => {
      renderMenu();
      menu.style.display = "block";

      // Position within bounds
      const rect = this.wrapper!.getBoundingClientRect();
      let left = x - rect.left;
      let top = y - rect.top;

      // Measure after display so dimensions are known
      const menuRect = menu.getBoundingClientRect();
      if (left + menuRect.width > rect.width) left = rect.width - menuRect.width - 4;
      if (top + menuRect.height > rect.height) top = rect.height - menuRect.height - 4;
      if (left < 0) left = 4;
      if (top < 0) top = 4;

      menu.style.left = `${left}px`;
      menu.style.top = `${top}px`;
    };

    const hideMenu = () => {
      menu.style.display = "none";
    };

    const getVideoPageUrl = (withTime: boolean): string => {
      const base = `https://d.tube/watch/${this.base58Id ?? videoId ?? ''}`;
      if (withTime && this.video) {
        const t = Math.floor(this.video.currentTime);
        return t > 0 ? `${base}?t=${t}` : base;
      }
      return base;
    };

    const copyToClipboard = (text: string, button: HTMLElement) => {
      navigator.clipboard.writeText(text).then(() => {
        const span = button.querySelector("span:last-child")!;
        const original = span.textContent;
        span.textContent = "Copied!";
        setTimeout(() => {
          span.textContent = original;
          hideMenu();
        }, 600);
      }).catch(() => hideMenu());
    };

    // Right-click handler
    this.wrapper.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      showMenu(e.clientX, e.clientY);
    });

    // Menu item clicks
    menu.addEventListener("click", (e) => {
      const button = (e.target as HTMLElement).closest(".dtube-context-menu-item") as HTMLElement;
      if (!button) return;
      e.stopPropagation();

      const action = button.dataset.action;
      switch (action) {
        case "loop":
          if (this.video) this.video.loop = !this.video.loop;
          renderMenu();
          break;
        case "copy-url":
          copyToClipboard(getVideoPageUrl(false), button);
          return; // Don't hide yet, copyToClipboard handles it
        case "copy-url-time":
          copyToClipboard(getVideoPageUrl(true), button);
          return;
        case "copy-embed":
          copyToClipboard(`<iframe src="https://play.d.tube/?v=${videoId}" width="640" height="360" frameborder="0" allowfullscreen></iframe>`, button);
          return;
        case "stats-nerds":
          hideMenu();
          this.toggleNerdStats();
          break;
        case "about":
          window.open("https://d.tube/about", "_blank", "noopener,noreferrer");
          hideMenu();
          break;
      }
    });

    // Hide on click inside player — capture phase to prevent Shaka play/pause
    this.wrapper.addEventListener("click", (e) => {
      if (menu.style.display === "block" && !menu.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
        hideMenu();
      }
    }, true);
    // Hide on click outside player
    document.addEventListener("click", (e) => {
      if (menu.style.display === "block" && !menu.contains(e.target as Node) && !this.wrapper?.contains(e.target as Node)) {
        hideMenu();
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") hideMenu();
    });
  }

  private toggleNerdStats(): void {
    if (this.nerdStatsPanel) {
      this.nerdStatsPanel.remove();
      this.nerdStatsPanel = null;
      if (this.nerdStatsInterval) {
        clearInterval(this.nerdStatsInterval);
        this.nerdStatsInterval = null;
      }
      return;
    }
    if (!this.wrapper) return;

    const panel = document.createElement("div");
    panel.className = "dtube-nerd-stats";

    const content = document.createElement("div");
    content.className = "dtube-nerd-stats-content";
    panel.appendChild(content);

    this.wrapper.appendChild(panel);
    this.nerdStatsPanel = panel;

    // Don't let clicks on the panel trigger play/pause
    panel.addEventListener("click", (e) => e.stopPropagation());

    this.updateNerdStats(content);
    this.nerdStatsInterval = setInterval(() => this.updateNerdStats(content), 1000);
  }

  private updateNerdStats(content: HTMLDivElement): void {
    if (!this.video || !this.player) return;

    const videoId = this.options.videoId;
    const stats = this.player.getStats();
    const tracks = this.player.getVariantTracks();
    const activeTrack = tracks.find((t) => t.active);
    const quality = this.video.getVideoPlaybackQuality?.();
    const formatBits = (bps: number): string => {
      if (bps < 1000) return `${Math.round(bps)} bps`;
      if (bps < 1e6) return `${(bps / 1000).toFixed(0)} Kbps`;
      return `${(bps / 1e6).toFixed(1)} Mbps`;
    };

    // Extract CDN hostname from source URL
    let cdnHost = "—";
    if (this.lastSrc) {
      try { cdnHost = new URL(this.lastSrc).hostname; } catch {}
    }

    const rows: [string, string][] = [];

    if (videoId) {
      rows.push(["Video ID", this.base58Id ?? "—"]);
      rows.push(["Video UUID", videoId]);
    }

    rows.push(["CDN", cdnHost]);

    if (activeTrack) {
      const vCodec = activeTrack.videoCodec || "—";
      const aCodec = activeTrack.audioCodec || "none";
      rows.push(["Codecs", `${vCodec} / ${aCodec}`]);
    }

    const wrapperRect = this.wrapper?.getBoundingClientRect();
    const viewport = wrapperRect ? `${Math.round(wrapperRect.width)}x${Math.round(wrapperRect.height)}` : "—";
    rows.push(["Resolution", `${this.video.videoWidth}x${this.video.videoHeight} / ${viewport}`]);
    // Calculate actual bitrate from segment downloads over last 10s
    const now = Date.now();
    const window = 30000;
    this.segmentLog = this.segmentLog.filter((s) => now - s.time < window);
    const totalBytes = this.segmentLog.reduce((sum, s) => sum + s.bytes, 0);
    const elapsed = this.segmentLog.length > 0 ? (now - this.segmentLog[0].time) / 1000 : 0;
    const actualBitrate = elapsed > 0 ? (totalBytes * 8) / elapsed : 0;

    rows.push(["Bitrate", `${formatBits(actualBitrate)} / ${formatBits(activeTrack?.bandwidth || 0)} max`]);
    rows.push(["Throughput", `${formatBits(stats.estimatedBandwidth)} est.`]);
    const p2pBuf = getP2PBufferAhead(this.video.currentTime);
    const p2pBufMB = (p2pBuf.bytes / (1024 * 1024)).toFixed(1);
    rows.push(["Buffer", `${p2pBuf.seconds.toFixed(1)}s (${p2pBuf.count} seg, ${p2pBufMB} MB)`]);

    if (quality) {
      const now = performance.now();
      if (this.lastFrameTime > 0) {
        const elapsed = (now - this.lastFrameTime) / 1000;
        const newFrames = quality.totalVideoFrames - this.lastFrameCount;
        if (elapsed > 0) this.currentFps = Math.round(newFrames / elapsed);
      }
      this.lastFrameCount = quality.totalVideoFrames;
      this.lastFrameTime = now;
      const fpsStr = this.currentFps > 0 ? ` @ ${this.currentFps}fps` : "";
      rows.push(["Frames", `${quality.droppedVideoFrames} dropped / ${quality.totalVideoFrames} total${fpsStr}`]);
    }

    rows.push(["Load Latency", `${stats.loadLatency.toFixed(2)}s`]);
    rows.push(["Player", `DTube ${DTUBE_PLAYER_VERSION} / Shaka ${shaka.Player.version}`]);

    content.innerHTML = rows
      .map(([label, value]) => `<div class="dtube-nerd-row"><span class="dtube-nerd-label">${label}</span><span class="dtube-nerd-value">${value}</span></div>`)
      .join("");
  }

  private bindEvents(): void {
    if (!this.video || !this.player) return;

    // Listen on the cast proxy's video element when available so play/pause/
    // ended/timeupdate fire whether the active playback is local or on a
    // remote receiver. Without this, the local <video> stays paused while
    // casting and callers (like the playlist auto-advance) never hear
    // ended events from the TV.
    const proxyVideo =
      (this.ui?.getControls?.()?.getCastProxy?.()?.getVideo() as HTMLMediaElement | undefined);
    const target: HTMLMediaElement = proxyVideo ?? this.video;

    target.addEventListener("play", () => this.options.onPlay?.());
    target.addEventListener("pause", () => this.options.onPause?.());
    target.addEventListener("ended", () => this.options.onEnded?.());
    target.addEventListener("timeupdate", () => {
      this.options.onTimeUpdate?.(target.currentTime ?? 0, target.duration ?? 0);
    });

    // Volume is local-only — we persist the local element's volume.
    this.video.addEventListener("volumechange", () => {
      this.saveVolume();
    });

    this.player.addEventListener("error", (event: Event) => {
      const shakaError = (event as CustomEvent).detail;
      // 3018 = TRANSMUXING_FAILED, caused by video-only TS streams
      // Retry with audio disabled so Shaka skips missing audio
      if (shakaError?.code === 3018 && this.player && !this.retryingWithoutAudio) {
        console.warn("Transmuxing failed, retrying without audio");
        this.retryingWithoutAudio = true;
        const resumeAt = this.video?.currentTime || undefined;
        this.player.unload().then(() => {
          this.player!.configure("manifest.disableAudio", true);
          return this.player!.load(this.lastSrc!, resumeAt);
        }).then(() => {
          this.attachTranscript();
        }).catch((retryError: Error) => {
          console.error("Retry also failed:", retryError);
          this.options.onError?.(retryError);
        });
        return;
      }
      if (shakaError?.code === 3018 && this.retryingWithoutAudio) return;
      // Recoverable errors (e.g. Shaka temporarily disabled a stream) fix
      // themselves — surfacing them would make the frontend reload the
      // video via the CDN fallback and lose the playback position.
      if (shakaError?.severity === shaka.util.Error.Severity.RECOVERABLE) {
        console.warn("Shaka recoverable error:", shakaError);
        return;
      }
      if (this.maybeRetryStreaming(shakaError)) return;
      console.error("Shaka error:", shakaError);
      this.options.onError?.(new Error(shakaError?.message || "Playback error"));
    });

    // Playback progressing again means the last outage is over — give the
    // next one a fresh retry budget.
    this.video.addEventListener("playing", () => {
      this.streamingRetryCount = 0;
    });
  }

  // Shaka treats VOD streaming failures as fatal once request retries are
  // exhausted (its default failureCallback only auto-retries live streams).
  // For transient network errors, retryStreaming() resumes from the current
  // position — the viewer sees a moment of buffering instead of the video
  // restarting from zero. Returns true when a retry was scheduled. After
  // MAX_STREAMING_RETRIES the error escalates to onError so the frontend
  // can fail over to the other CDN (which now preserves the position too).
  private static readonly MAX_STREAMING_RETRIES = 3;

  private maybeRetryStreaming(shakaError: { category?: number; code?: number } | undefined): boolean {
    if (!this.player || !shakaError) return false;
    const { Category, Code } = shaka.util.Error;
    if (shakaError.category !== Category.NETWORK) return false;
    const retriable =
      shakaError.code === Code.BAD_HTTP_STATUS ||
      shakaError.code === Code.HTTP_ERROR ||
      shakaError.code === Code.TIMEOUT;
    if (!retriable) return false;
    if (this.streamingRetryCount >= DTubePlayer.MAX_STREAMING_RETRIES) return false;
    this.streamingRetryCount++;
    const delay = 2 ** (this.streamingRetryCount - 1); // 1s, 2s, 4s
    console.warn(
      `DTube player: network error (code ${shakaError.code}), retrying streaming in ${delay}s ` +
      `(attempt ${this.streamingRetryCount}/${DTubePlayer.MAX_STREAMING_RETRIES})`,
    );
    return this.player.retryStreaming(delay);
  }

  private restoreVolume(): void {
    if (!this.video) return;

    try {
      const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
      if (saved) {
        const volume = parseFloat(saved);
        if (!isNaN(volume) && volume >= 0 && volume <= 1) {
          this.video.volume = volume;
        }
      }
    } catch {
      // localStorage not available
    }
  }

  private saveVolume(): void {
    if (!this.video) return;

    try {
      localStorage.setItem(VOLUME_STORAGE_KEY, this.video.volume.toString());
    } catch {
      // localStorage not available
    }
  }

  private loadBandwidthEstimate(): number {
    try {
      const saved = Number(localStorage.getItem(BANDWIDTH_STORAGE_KEY));
      if (saved >= MIN_SAVED_BANDWIDTH && saved <= MAX_SAVED_BANDWIDTH) {
        return saved;
      }
    } catch {
      // localStorage not available
    }
    return DEFAULT_BANDWIDTH_ESTIMATE;
  }

  private saveBandwidthEstimate(): void {
    if (!this.player) return;
    const bw = this.player.getStats().estimatedBandwidth;
    if (!Number.isFinite(bw) || bw <= 0) return;
    const clamped = Math.max(MIN_SAVED_BANDWIDTH, Math.min(MAX_SAVED_BANDWIDTH, bw));
    try {
      localStorage.setItem(BANDWIDTH_STORAGE_KEY, String(Math.round(clamped)));
    } catch {
      // localStorage not available
    }
  }

  // Public API methods

  public play(): void {
    this.video?.play();
  }

  public pause(): void {
    this.video?.pause();
  }

  public toggle(): void {
    if (this.video?.paused) {
      this.video.play();
    } else {
      this.video?.pause();
    }
  }

  public seek(time: number): void {
    if (this.video) {
      this.video.currentTime = time;
    }
  }

  public getCurrentTime(): number {
    return this.video?.currentTime ?? 0;
  }

  public getDuration(): number {
    return this.video?.duration ?? 0;
  }

  public setVolume(volume: number): void {
    if (this.video) {
      this.video.volume = Math.max(0, Math.min(1, volume));
    }
  }

  public getVolume(): number {
    return this.video?.volume ?? 1;
  }

  public mute(): void {
    if (this.video) {
      this.video.muted = true;
    }
  }

  public unmute(): void {
    if (this.video) {
      this.video.muted = false;
    }
  }

  public isMuted(): boolean {
    return this.video?.muted ?? false;
  }

  public setPlaybackRate(rate: number): void {
    if (this.video) {
      this.video.playbackRate = rate;
    }
  }

  public getPlaybackRate(): number {
    return this.video?.playbackRate ?? 1;
  }

  public enterFullscreen(): void {
    this.container.requestFullscreen?.();
  }

  public exitFullscreen(): void {
    document.exitFullscreen?.();
  }

  public isFullscreen(): boolean {
    return document.fullscreenElement === this.container;
  }

  public setPoster(url: string): void {
    if (this.video) {
      this.video.poster = url;
    }
  }

  // resumeAt: start position in seconds — pass it when swapping to another
  // source of the SAME video (e.g. CDN failover) so playback continues where
  // it left off. Omit for a different video or the ad→content switch.
  public async setSource(src: string, resumeAt?: number): Promise<void> {
    this.lastSrc = src;
    if (!this.player) return;
    this.streamingRetryCount = 0;
    try {
      await this.getActivePlayer().load(src, resumeAt);
      this.attachTranscript();
    } catch (error) {
      console.error("Error loading source:", error);
      this.options.onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }

  public getVideoElement(): HTMLVideoElement | null {
    return this.video;
  }

  public getP2PStats(): P2PStats {
    return getP2PStats();
  }

  public onP2PStatsChange(callback: (stats: P2PStats) => void): void {
    onStatsChange(callback);
  }

  public async dispose(): Promise<void> {
    this.saveBandwidthEstimate();
    this.hideCastOverlay();
    if (this.bandwidthSaveInterval) {
      clearInterval(this.bandwidthSaveInterval);
      this.bandwidthSaveInterval = null;
    }
    if (this.nerdStatsInterval) {
      clearInterval(this.nerdStatsInterval);
      this.nerdStatsInterval = null;
    }
    if (this.p2pEngine) {
      await this.p2pEngine.destroy();
      this.p2pEngine = null;
    }
    if (this.ui) {
      this.ui.destroy();
      this.ui = null;
    }
    if (this.player) {
      await this.player.destroy();
      this.player = null;
    }
    if (this.video) {
      this.video.remove();
      this.video = null;
    }
  }
}

export default DTubePlayer;
